import React, { useState, useEffect } from "react";
import Media from "@qlik/qlik/src/components/Media";
import { formatDate, formatDateRange } from "@qlik/qlik/src/lib/util/dates";
import {
    filterValidCta,
    getArticleReadingStats,
    getCardDescriptionText,
    getCardEyebrowText,
    getCardMedia,
    getInternalLinkCta,
    getResourceInternalLinkCta,
    renderSanitizedHtmlContent,
} from "@qlik/qlik/src/lib/util/helpers";
import Cta from "@qlik/qlik/src/components/Cta";
import RichText from "@qlik/qlik/src/components/RichText";

import {
    TYPE_BLOG,
    TYPE_EVENT,
    TYPE_CARD,
    TYPE_MICRO_ASSET,
    TYPE_RESOURCE,
} from "@qlik/qlik/src/lib/constants/contentful-types";
import { BLOG_PATHNAME } from "@qlik/qlik/src/lib/constants/blog-constants";
import * as styles from "./styles.module.css";
import { useI18n } from "@qlik/utils/i18nContext";

/**
 * CardWrapper component.
 * Wraps entire card in a link for Resource Blog type.
 * @param {Object} props - The props for the CardWrapper component.
 * @returns {JSX.Element} - The CardWrapper component.
 */
const CardWrapper = ({ isWholeCardClickable, className, linkData, children }) => {
    const internalLinkSlug = linkData?.internalLink?.slug?.startsWith("/")
        ? linkData?.internalLink?.slug
        : `/${linkData?.internalLink?.slug}`;
    const internalLink =
        linkData?.internalLink?.__typename === TYPE_MICRO_ASSET // eslint-disable-line no-underscore-dangle
            ? linkData?.internalLink?.asset[0]?.original_secure_url
            : internalLinkSlug;

    return isWholeCardClickable && !!linkData && (internalLink || linkData.externalUrl) ? (
        <Cta data={linkData} className={`block hover:no-underline ${className}`}>
            {children}
        </Cta>
    ) : (
        <div className={className}>{children}</div>
    );
};

/**
 * ImageCard component.
 * Renders a single image card with different styles based on the type.
 * @param {Object} props - The props for the ImageCard component.
 * @returns {JSX.Element} - The ImageCard component.
 */
const ImageCard = ({ data, isGridTight, additionalClasses, isImageFull }) => {
    const { title, subtitle, cta, __typename, body, slug, author, microcopyGroup } = data ?? {};

    const [formattedDateStr, setFormattedDateStr] = useState("");
    const [isMounted, setMounted] = useState(false);

    // Get the i18n information for formatting dates
    const { i18n } = useI18n();

    useEffect(() => {
        if (__typename === TYPE_EVENT) {
            setFormattedDateStr(formatDateRange(data.startDate, data.endDate, i18n.curr.langCode));
        } else if (data.dateTime) {
            setFormattedDateStr(formatDate(data.dateTime, i18n.curr.langCode));
        }
    }, [data.dateTime, data.startDate, data.endDate, __typename]);

    useEffect(() => {
        setMounted(true);
    }, []);

    // remove margin from subtitle if no date is rendered
    const subtitleClassName = formattedDateStr ? "mb-2 lg:mb-2.5" : "";

    const cardDescriptionText = getCardDescriptionText(data);

    let titleClassName = cardDescriptionText ? "lg:mt-5" : "lg:mt-5 lg:mb-7.5";

    // if no date & no subtitle adjust gaps of the title
    if (!formattedDateStr && !subtitle) {
        titleClassName = "lg:mt-0 ";
    }

    if (formattedDateStr && __typename === TYPE_RESOURCE) {
        titleClassName = "lg:mt-0 ";
    }

    let wrapperClassName = "";
    // variant 2 specific styles
    if (__typename === TYPE_EVENT) {
        titleClassName += "line-clamp-2";
        wrapperClassName = "lg:hover:shadow-lg transition-shadow duration-300";
    }

    let ctaList = filterValidCta(cta);

    // add redirect CTA for resource cards
    if (__typename === TYPE_RESOURCE) {
        ctaList = getResourceInternalLinkCta(data, ctaList);
    }

    // variant 4 specific styles
    if (__typename === TYPE_BLOG) {
        titleClassName += " line-clamp-4";
        ctaList = getInternalLinkCta(BLOG_PATHNAME, slug, TYPE_BLOG, microcopyGroup, ctaList?.[0]);
    }

    const cardImage = getCardMedia(data);
    let isWholeCardClickable = ctaList.length === 1;
    // Set the whole card to be non-clickable if the media type is a video
    if (cardImage?.desktopMedia?.[0]?.resource_type === "video" || cardImage?.mediaCoverImage?.length > 0) {
        isWholeCardClickable = false;
    }

    // variant 5 specific styles
    let imgWrapperClassName = `${isImageFull ? "md:h-[250px]" : "max-h-38 sm:max-h-80 md:h-32 lg:h-44 xl:h-60"}`;

    const infoWrapperClassName = isImageFull
        ? "flex flex-col p-5 lg:p-10 flex-1"
        : `flex flex-col p-3 xl:p-5 flex-grow ${
              cardImage?.desktopMedia?.[0]?.secure_url ? "pt-5 lg:pt-10 xl:pt-10" : "lg:pt-5"
          }`;

    let imgClassName = "lg:group-hover:scale-105 transition-all duration-300";
    if (isGridTight) {
        if (isImageFull) {
            imgWrapperClassName = "md:h-[250px]";
        } else {
            imgWrapperClassName = "max-h-38 sm:max-h-41.75 h-32 lg:h-41.75";
        }
        imgClassName = "";
        titleClassName += "mb-7.5";
        wrapperClassName = "lg:hover:shadow-none";
    }

    const cardEyebrowText = getCardEyebrowText(data);
    const cardWithPadding = `${additionalClasses} group border border-stone/50 bg-white rounded p-3 lg:pb-4.5 lg:pt-4.75 lg:px-4.75 hover:no-underline group/resource-card lg:hover:shadow-lg transition-shadow duration-300 flex flex-col`;
    const cardWithoutPadding = `${additionalClasses}  flex flex-col p-0 shadow-lg rounded overflow-hidden group/resource-card ${
        ctaList.length > 0 ? "lg:hover:shadow-xl group" : ""
    } `;
    let cardDescriptionTextClassNames = "";

    if (isImageFull && ctaList.length > 0) {
        cardDescriptionTextClassNames = "my-2.5 lg:my-7.5";
    } else if (isImageFull && ctaList.length === 0) {
        cardDescriptionTextClassNames = "mt-2.5 lg:mt-7.5 mb-0";
    } else {
        cardDescriptionTextClassNames = "mb-2 lg:mb-7.5";
    }

    return (
        <CardWrapper
            className={`bg-white ${isImageFull ? cardWithoutPadding : cardWithPadding} ${wrapperClassName} ${
                isWholeCardClickable ? "after:hidden h-full" : ""
            }`}
            isWholeCardClickable={isWholeCardClickable}
            linkData={ctaList[0]}
        >
            {cardImage && (
                <div className={`overflow-hidden ${imgWrapperClassName}`}>
                    <Media media={cardImage} className={`w-full h-full object-cover ${imgClassName}`} />
                </div>
            )}
            <div className={`${infoWrapperClassName}`}>
                {/* meta details */}
                {__typename !== TYPE_BLOG
                    ? subtitle &&
                      !isImageFull && (
                          <p className={`tag-2 tracking-wide4 uppercase ${subtitleClassName}`}>{subtitle}</p>
                      )
                    : null}
                {!isGridTight && cardEyebrowText && <p className="tag-2 uppercase text-sm mb-5">{cardEyebrowText}</p>}

                {/* do not render datetime for resources */}
                {formattedDateStr && __typename !== TYPE_RESOURCE && (
                    <span className="tag-2 text-gray/50 uppercase tracking-wide4">{formattedDateStr}</span>
                )}
                {title && !isImageFull && (
                    <p className={`title-5 mt-2 mb-3.75 text-gray text-md lg:text-xl line-clamp-3 ${titleClassName}`}>
                        {title}
                    </p>
                )}
                {title && isImageFull && (
                    <p
                        className={`${styles.cardTitles} title-4`}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(title) }}
                    />
                )}
                {isImageFull && subtitle && <p className="title-5 mt-1.5 md:mt-2.5 text-gray">{subtitle}</p>}
                {__typename !== TYPE_BLOG
                    ? subtitle && data.body?.raw && isMounted && <RichText data={data} className="mb-8 lg:mb-7.5" />
                    : null}
                {__typename !== TYPE_BLOG
                    ? cardDescriptionText && <p className={cardDescriptionTextClassNames}>{cardDescriptionText}</p>
                    : null}
                {/* Render a div with content only if both 'bodyDescription' and 'data.body.raw' are present and truthy */}
                {__typename === TYPE_CARD && data.body?.raw && isMounted ? (
                    <RichText data={data} className={ctaList.length > 0 ? "mb-8 lg:mb-7.5" : styles.bodyMargin} />
                ) : null}

                {/* author with read time */}
                {author && (
                    <div className="flex items-center gap-x-2.5 mb-7.5">
                        {author?.avatar && (
                            <Media media={author.avatar} className="object-cover rounded-xl h-[25px] w-[25px]" />
                        )}
                        <span className="tag-3">{`${author.firstName ?? ""} ${author.lastName ?? ""}`}</span>
                        {/* read time */}
                        {__typename !== TYPE_BLOG
                            ? body && (
                                  <>
                                      <span className="w-1 h-1 rounded-xl bg-ocean" />
                                      <span className="tag-3 text-ocean">
                                          {getArticleReadingStats(body.raw).readTime}
                                      </span>
                                  </>
                              )
                            : null}
                    </div>
                )}

                {/* pro links - only render as links if card is not a link already */}
                {ctaList.length > 0 ? (
                    <div className={`mt-auto flex flex-col gap-3.75 ${!data.body?.raw ? "pt-3.75" : ""}`}>
                        {ctaList.map((linkData) =>
                            isWholeCardClickable ? (
                                <span
                                    key={linkData.id}
                                    className="green-link text-sm lg:group-hover/resource-card:text-dark-green lg:group-hover/resource-card:after:arrow-green-dark lg:group-hover/resource-card:after:translate-x-0.75"
                                    dangerouslySetInnerHTML={{ __html: renderSanitizedHtmlContent(linkData.text) }}
                                />
                            ) : (
                                <Cta key={linkData.id} className="text-sm green-link" data={linkData} />
                            )
                        )}
                    </div>
                ) : null}
            </div>
        </CardWrapper>
    );
};

export default ImageCard;
